import { Injectable, inject } from '@angular/core';
import { catchError, map, type Observable, of } from 'rxjs';
import type { DevicesPayload } from '../../../equipment/devices/components/devices.models';
import { CustomersApiService } from '../api/customers-api.service';
import type { Customer, CustomerFiltersResponse, CustomerTable } from '../customers.types';
import { CustomerListResponse, CustomerResponse } from '../api/customers-api.type';
import { TimezoneService } from 'src/app/services/utility/timezone.service';

@Injectable({
  providedIn: 'root'
})
export class CustomersService {
  private readonly customersApiService = inject(CustomersApiService);
  private readonly timezoneService = inject(TimezoneService);

  getTableData(tableState: DevicesPayload): Observable<CustomerTable> {
    return this.customersApiService.getList(tableState).pipe(map(response => this.convertToCustomerTable(response)));
  }

  getCustomerFiltersItems(): Observable<CustomerFiltersResponse> {
    return this.customersApiService
      .getFilters()
      .pipe(catchError(() => of({ customerTypes: [], accountStatusTypes: [], commodities: [] })));
  }

  convertToCustomerTable = ({ items, totalCount }: CustomerListResponse): CustomerTable => {
    const data = items.map(this.convertToCustomer);
    return { data, total: totalCount };
  };

  convertToCustomer = (customer: CustomerResponse): Customer => {
    const accountNumbers: string[] = [];
    const commodities: string[] = [];
    const serviceLocationIds: string[] = [];
    const serviceLocationAddresses: (string | null)[] = [];

    for (const account of customer.accounts) {
      account.serviceDetails.forEach((detail, detailIdx) => {
        detail.serviceLocationIds.forEach((locId, locIdx) => {
          accountNumbers.push(
            detailIdx === 0 && locIdx === 0 ? `${account.accountNumber} / ${account.accountSubNumber}` : ''
          );
          commodities.push(locIdx === 0 ? detail.commodity : '');
          serviceLocationIds.push(locId);
          serviceLocationAddresses.push(detail.serviceLocationAddresses[locIdx] ?? null);
        });
      });
    }

    return {
      ...customer,
      accountNumber: accountNumbers,
      accountStatus: customer.accounts.map(({ accountStatus }) => accountStatus),
      commodities,
      customerNumber: `${customer.customerNumber} / ${customer.customerSubNumber}`,
      customerOriginalNumber: customer.customerNumber,
      customerUrlNumber: `${customer.customerNumber}-${customer.customerSubNumber}`,
      lastName: this.prepareCustomerFullName(customer.firstName ?? '', customer.lastName ?? ''),
      serviceLocationId: serviceLocationIds,
      serviceLocationAddress: serviceLocationAddresses
    };
  };

  prepareCustomerFullName(firstName: string, lastName: string): string {
    return [lastName, firstName].filter(Boolean).join(', ');
  }
}
